// src/pages/notifications/ReportsPresentations/PresentationExecutionHistory.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../../../styles/pages/notifications/InsightItemConfig.scss';
import '../../../styles/pages/notifications/reportsPresentations/presentationExecutionHistory.scss';
import { clockSVG, documentSVG } from '../../../lib/allCustomSVGs';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import NoData from '../../../ui/noData/NoData';
import PaginationMain from '../../../components/pagination/PaginationMain';

import get_presentation_execution_history_api_call, {
  PresentationExecutionHistoryItem,
  PresentationExecutionHistoryPagination,
} from '../../../lib/api/presentations/presentation-execution-history-api-call';

type Props = {
  togglePopupModal: (isOpen: boolean) => void;
};

type LoadState = 'pending' | 'success' | 'error' | 'null';

const ITEMS_PER_PAGE = 5; // fixed page size

const getStatusInfo = (status: string) => {
  // Validate input
  if (!status || typeof status !== 'string' || status.trim() === '') {
    return { className: 'other', label: '' };
  }

  // Determine CSS class: success, failed, or other
  let className = 'other';
  const statusLower = status.toLowerCase();

  if (statusLower === 'completed') {
    className = 'success';
  } else if (statusLower === 'failed') {
    className = 'failed';
  }

  // Clean and format label: remove dashes, underscores, etc.
  const label = status.replace(/[-_]/g, ' ');

  return { className, label };
};

const formatDateTime = (iso: string) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const PresentationExecutionHistory = ({ togglePopupModal }: Props) => {
  const { t } = useTranslation();

  const [items, setItems] = useState<PresentationExecutionHistoryItem[]>([]);
  const [pagination, setPagination] =
    useState<PresentationExecutionHistoryPagination | null>(null);
  const [loading, setLoading] = useState<LoadState>('pending');

  // 0-based page index for PaginationMain (mirrors your reference)
  const [currentPage, setCurrentPage] = useState<number>(0);

  // Guards against loops / double invokes
  const didInitRef = useRef(false);
  const inFlightRef = useRef(false);

  const fetchHistory = async (zeroBasedPage: number) => {
    // Prevent re-entrant / rapid double calls
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    setLoading('pending');
    try {
      const apiPage = zeroBasedPage + 1; // API expects 1-based
      const res = await get_presentation_execution_history_api_call(
        apiPage,
        ITEMS_PER_PAGE
      );

      if (res && res.items) {
        setItems(res.items);
        setPagination(res.pagination);
        setLoading(res.items.length > 0 ? 'success' : 'null');
      } else {
        setItems([]);
        setPagination(null);
        setLoading('null');
      }
    } catch (e) {
      console.error('Error fetching presentation execution history:', e);
      setItems([]);
      setPagination(null);
      setLoading('error');
    } finally {
      inFlightRef.current = false;
    }
  };

  // Fetch once per mount (like your reference modal popups)
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    fetchHistory(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty to avoid effect churn

  const pageCount = useMemo(
    () => pagination?.last_page ?? 1,
    [pagination?.last_page]
  );

  const totalRecords = useMemo(
    () => pagination?.total ?? 0,
    [pagination?.total]
  );

  const handlePageClick = (event: { selected: number }) => {
    const next = event.selected;
    if (next === currentPage) return; // no-op if same page
    setCurrentPage(next);
    fetchHistory(next);
  };

  return (
    <div style={{ width: '100%' }} className='insightItemConfig'>
      <div className='insightItemConfig__title'>
        <div className='insightItemConfig__title--main'>
          <h3>{t('PresentationExecutionHistory')}</h3>
        </div>
        <p>{t('PresentationExecutionHistoryDescription')}</p>
      </div>

      {loading === 'pending' && (
        <div className='containerWithPaddingTopOnMobile'>
          <LoadingSpinner />
        </div>
      )}

      {loading === 'error' && (
        <div className='containerWithPaddingTopOnMobile'>
          <NoData
            title={t('ErrorFetchingData')}
            subTitle={t('SomethingWentWrong-msg')}
          />
        </div>
      )}

      {loading === 'null' && (
        <div className='containerWithPaddingTopOnMobile'>
          <NoData
            title={t('NoHistoryFound')}
            subTitle={t('NoPresentationHistoryDescription')}
          />
        </div>
      )}

      {loading === 'success' && (
        <>
          <div className='presentationExecutionHistory'>
            {items.map((it) => {
              const { className: tagClass, label: tagText } = getStatusInfo(
                it.status
              );

              return (
                <div
                  key={it.id}
                  className={`presentationExecutionHistory__item ${
                    tagClass === 'failed' ? 'failed' : ''
                  }`}
                >
                  <div className='presentationExecutionHistory__item--main'>
                    <div className='presentationExecutionHistory__item--main-left'>
                      <div className='presentationExecutionHistory__item--main-left-title'>
                        <CustomSVGs svg={documentSVG} />
                        <h3>{it.presentation_name}</h3>
                        <div
                          className={`presentationExecutionHistory__item--main-left-title-tag ${tagClass}`}
                        >
                          <span>{tagText}</span>
                        </div>
                      </div>

                      <div className='presentationExecutionHistory__item--main-left-description'>
                        <CustomSVGs svg={clockSVG} />
                        <span>{formatDateTime(it.executed_at)}</span>
                      </div>

                      <div className='presentationExecutionHistory__item--main-left-info'>
                        {it.duration_seconds
                          ? `Duration: ${it.duration_seconds}s`
                          : ''}
                        {/*   {it.batch_identifier
                          ? ` • Batch: ${it.batch_identifier}`
                          : ''} */}
                      </div>
                    </div>

                    <div className='presentationExecutionHistory__item--main-right'>
                      <MainSquareButton
                        onClick={(e) => {
                          e.preventDefault();
                          if (it.presentation_url) {
                            window.open(it.presentation_url, '_blank');
                          }
                        }}
                        disabled={!it.presentation_url}
                        type='button'
                        buttonType='white'
                        value={t('View')}
                        normal
                      />
                    </div>
                  </div>

                  {it.status === 'failed' && it.error_message && (
                    <div className='presentationExecutionHistory__item--errorMessage'>
                      <span>{it.error_message}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination — exactly like your reference */}
          {totalRecords > 0 && (
            <div
              className='shortcodesPopup__pagination'
              style={{ marginTop: '1.25rem' }}
            >
              <PaginationMain
                key='presentation-history-pagination'
                totalRecords={totalRecords}
                itemsPerPage={ITEMS_PER_PAGE}
                onRecordsPerPageChange={() => {}}
                breakLabel='...'
                nextLabel={t('Next >')}
                onPageChange={handlePageClick}
                pageRangeDisplayed={2}
                initialPage={currentPage}
                pageCount={pageCount}
                previousLabel={t('< Previous')}
                marginPagesDisplayed={2}
                containerClassName='pagination'
                pageLinkClassName='pagination__page-num'
                previousLinkClassName='pagination__page-num'
                nextLinkClassName='pagination__page-num'
                disabledLinkClassName='pagination__page-num-disabled'
                activeLinkClassName='pagination__active'
              />
            </div>
          )}
        </>
      )}

      <div
        style={{ marginTop: '2.5rem' }}
        className='insightItemConfig__buttons'
      >
        <MainSquareButton
          value={t('Close')}
          type='button'
          buttonType='white'
          disabled={false}
          onClick={() => togglePopupModal(false)}
        />
      </div>
    </div>
  );
};

export default PresentationExecutionHistory;
